import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1547895749-888a559fc2a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlfGVufDB8fHx8MTc4NDU4MDgwOXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotUser, setForgotUser] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await login(username, password); navigate("/"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || "Error de conexión"); }
    finally { setLoading(false); }
  };

  const forgot = async () => {
    try {
      await api.post("/auth/forgot-password", { username: forgotUser });
      toast.success("Solicitud enviada. Un administrador restablecerá tu contraseña.");
      setForgotOpen(false); setForgotUser("");
    } catch { toast.success("Solicitud enviada."); setForgotOpen(false); }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <div className="w-full lg:w-[46%] flex flex-col justify-center px-6 sm:px-14 py-10">
        <div className="max-w-sm w-full mx-auto">
          <div className="flex items-center gap-3 mb-10 selec-fade-up">
            <img src="/logo-selec.jpg" alt="SELEC" className="h-14 w-14 rounded-2xl object-cover shrink-0 shadow-elevated" />
            <div className="leading-none">
              <p className="font-heading font-black text-2xl tracking-tight">SELEC <span className="text-primary">ONE 360</span></p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mt-1">La fuerza detrás de tu producción</p>
            </div>
          </div>

          <h1 className="font-heading text-3xl font-black tracking-tight mb-1 selec-fade-up" style={{ animationDelay: "0.08s" }}>Bienvenido</h1>
          <p className="text-sm text-muted-foreground mb-8 selec-fade-up" style={{ animationDelay: "0.14s" }}>Ingresa con tu nombre de usuario asignado.</p>

          <form onSubmit={submit} className="space-y-5 selec-fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="space-y-2">
              <Label htmlFor="username" className="text-xs font-bold uppercase tracking-widest">Usuario</Label>
              <Input id="username" data-testid="login-username" autoFocus value={username} onChange={(e) => setUsername(e.target.value)} placeholder="usuario asignado" className="rounded-xl h-11" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-widest">Contraseña</Label>
              <Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="rounded-xl h-11" required />
            </div>

            {error && <p className="text-sm text-destructive font-medium border border-destructive/30 bg-destructive/10 px-3 py-2 rounded-xl" data-testid="login-error">{error}</p>}

            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 rounded-xl font-bold uppercase tracking-wider text-sm">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ingresar"}
            </Button>
          </form>

          <button className="mt-6 text-xs text-primary font-semibold" onClick={() => setForgotOpen(true)} data-testid="forgot-password-link">
            ¿Olvidaste tu contraseña?
          </button>
        </div>
      </div>

      <div className="hidden lg:block relative flex-1">
        <img src={BG} alt="Almacén industrial" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/40 mix-blend-multiply" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute bottom-0 left-0 p-14 text-white max-w-lg selec-fade-up" style={{ animationDelay: "0.3s" }}>
          <p className="text-[11px] uppercase tracking-[0.3em] font-bold mb-3 text-white/80">SELEC — Soluciones Eléctricas</p>
          <h2 className="font-heading text-4xl font-black leading-tight tracking-tight">Control total de tu empresa de materiales eléctricos.</h2>
          <p className="mt-4 text-white/70 text-sm">CRM + ERP unificado: comercial, administración, finanzas, operaciones y logística en un solo sistema.</p>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Recuperar contraseña</DialogTitle>
            <DialogDescription>Ingresa tu usuario. Un administrador restablecerá tu contraseña.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-widest">Usuario</Label>
            <Input className="rounded-xl" value={forgotUser} onChange={(e) => setForgotUser(e.target.value)} data-testid="forgot-username" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setForgotOpen(false)}>Cancelar</Button>
            <Button className="rounded-xl font-bold" onClick={forgot} disabled={!forgotUser} data-testid="forgot-submit">Enviar solicitud</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
